import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GamepadIcon from '@mui/icons-material/Gamepad';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import { WithTranslation, withTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';

import { FeedbackIcon, HandPalmIcon } from '@icons';
import { reportIssueFeatureEnabled } from '@components/ReportItDialog';
import { useMytaverseEvent } from '@core/dashboard/providers';

import {
  ArrowCluster,
  IconRing,
  KeyCap,
  MouseShape,
  WasdCluster,
} from './KeyVisuals';
import { HELP_GUIDE_TABS, HOTKEY_GROUPS } from './constants';
import { HelpGuideTabEnum, IHelpGuideModal } from './interfaces';
import { useStyles } from './styles';

const HelpGuideModal = ({
  open,
  onClose,
  t: translate,
}: IHelpGuideModal & WithTranslation) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(HelpGuideTabEnum.CONTROLS);
  const eventContext = useMytaverseEvent();
  const disabledKeyCommands = eventContext?.currentEvent?.disabledKeyCommands;

  useEffect(() => {
    if (open) {
      setActiveTab(HelpGuideTabEnum.CONTROLS);
    }
  }, [open]);

  const isBindEnabled = ({ key }: { key: string }) => {
    if (!disabledKeyCommands?.length) return true;

    const disabled = disabledKeyCommands.map((command) =>
      command.toLowerCase(),
    );
    const aliases =
      key === 'Space' ? ['space', 'spacebar'] : [key.toLowerCase()];

    return !aliases.some((alias) => disabled.includes(alias));
  };

  const hotkeyGroups = HOTKEY_GROUPS.map((group) => ({
    ...group,
    binds: group.binds.filter(isBindEnabled),
  })).filter((group) => group.binds.length > 0);

  const rows = useMemo(() => {
    const controls = isMobile
      ? [
          {
            key: 'moveTouch',
            visual: (
              <IconRing>
                <GamepadIcon />
              </IconRing>
            ),
          },
          {
            key: 'lookTouch',
            visual: (
              <IconRing>
                <TouchAppIcon />
              </IconRing>
            ),
          },
        ]
      : [
          { key: 'move', visual: <WasdCluster /> },
          { key: 'look', visual: <MouseShape /> },
          { key: 'rotate', visual: <ArrowCluster /> },
          { key: 'fullscreen', visual: <KeyCap label="F" accent /> },
        ];

    const assistance = [
      {
        key: 'assistant',
        visual: (
          <IconRing>
            <PersonOutlineIcon />
          </IconRing>
        ),
      },
      ...(reportIssueFeatureEnabled()
        ? [
            {
              key: 'reportIssue',
              visual: (
                <IconRing>
                  <FeedbackIcon />
                </IconRing>
              ),
            },
          ]
        : []),
    ];

    return {
      [HelpGuideTabEnum.CONTROLS]: controls,
      [HelpGuideTabEnum.HOTKEYS]: [],
      [HelpGuideTabEnum.POINTS_OF_INTEREST]: [
        {
          key: 'infoHotspots',
          visual: (
            <IconRing>
              <InfoOutlinedIcon />
            </IconRing>
          ),
        },
        {
          key: 'handHotspots',
          visual: (
            <IconRing>
              <HandPalmIcon />
            </IconRing>
          ),
        },
      ],
      [HelpGuideTabEnum.ASSISTANCE]: assistance,
    };
  }, []);

  return (
    <Modal sx={classes.root} open={open} onClose={onClose}>
      <Box
        component="div"
        sx={classes.container}
        data-testid="help-guide-modal"
      >
        <Box sx={classes.header}>
          <Box>
            <Typography sx={classes.headerLabel}>
              {translate('helpGuide.label')}
            </Typography>
            <Typography sx={classes.headerTitle}>
              {translate('helpGuide.title')}
            </Typography>
          </Box>
          <IconButton
            sx={classes.closeButton}
            onClick={onClose}
            aria-label={translate('close')}
            data-testid="help-guide-close-btn"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={classes.body}>
          <Box sx={classes.rail}>
            {HELP_GUIDE_TABS.map(({ id, labelKey, icon }) => (
              <ButtonBase
                key={id}
                sx={{
                  ...classes.railTab,
                  ...(activeTab === id ? classes.railTabActive : {}),
                }}
                onClick={() => setActiveTab(id)}
                data-testid={`help-guide-tab-${id}`}
              >
                {icon}
                {translate(labelKey)}
              </ButtonBase>
            ))}
          </Box>
          <Box sx={classes.pane}>
            {activeTab === HelpGuideTabEnum.HOTKEYS
              ? hotkeyGroups.map(({ labelKey, binds }) => (
                  <Box key={labelKey} sx={classes.hotkeyGroup}>
                    <Typography sx={classes.hotkeyGroupLabel}>
                      {translate(labelKey)}
                    </Typography>
                    <Box sx={classes.hotkeyGrid}>
                      {binds.map((bind) => (
                        <Box key={bind.key} sx={classes.hotkeyRow}>
                          <KeyCap label={bind.key} />
                          <Box>
                            <Typography sx={classes.hotkeyLabel}>
                              {translate(`${bind.labelKey}.title`)}
                            </Typography>
                            <Typography sx={classes.hotkeyDescription}>
                              {translate(`${bind.labelKey}.description`)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))
              : rows[activeTab].map(({ key, visual }) => (
                  <Box key={key} sx={classes.row}>
                    <Box sx={classes.rowVisual}>{visual}</Box>
                    <Box>
                      <Typography sx={classes.rowTitle}>
                        {translate(`helpGuide.${key}.title`)}
                      </Typography>
                      <Typography sx={classes.rowDescription}>
                        {translate(`helpGuide.${key}.description`)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
          </Box>
        </Box>
        {!isMobile && (
          <Box sx={classes.footer}>
            <Box sx={classes.tipBadge}>{translate('helpGuide.tipBadge')}</Box>
            <Typography sx={classes.tipText}>
              {translate('helpGuide.tipBefore')}
              <Box component="span" sx={classes.inlineKey}>
                F
              </Box>
              {translate('helpGuide.tipAfter')}
            </Typography>
            <Typography sx={classes.footerHint}>
              {translate('helpGuide.escClose')}
            </Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default withTranslation()(HelpGuideModal);
